import { userApiService } from "./userApiService";
import {
  enrichPumpRunSensors,
  normalizePumpRun,
  normalizeTimestampMs,
  relayLogsFromCommands,
  sanitizeSensorSnapshot,
  synthesizePumpRunsFromCommands,
} from "../utils/pumpActivityUtils";

const HISTORY_POLL_MS = 15000;

function runMergeKey(r = {}) {
  const cmdId = r.onCommandId || (r.synthetic ? r.id : "");
  if (cmdId) return `cmd:${cmdId}`;
  const q = normalizeTimestampMs(r.queuedAt);
  const src = r.sourceLabel || r.source || "";
  if (q) return `t:${src}:${Math.floor(q / 60000)}`;
  const s = normalizeTimestampMs(r.startedAt);
  if (s) return `s:${src}:${Math.floor(s / 60000)}`;
  return `id:${r.id}`;
}

function dedupeNearPumpRuns(runs = []) {
  const sorted = [...runs].sort((a, b) => (Number(b.queuedAt) || 0) - (Number(a.queuedAt) || 0));
  const kept = [];
  for (const run of sorted) {
    const label = run.sourceLabel || run.source || "";
    const at = normalizeTimestampMs(run.queuedAt || run.startedAt);
    const dup = kept.some((row) => {
      const label2 = row.sourceLabel || row.source || "";
      if (label !== label2) return false;
      const at2 = normalizeTimestampMs(row.queuedAt || row.startedAt);
      return at && at2 && Math.abs(at - at2) <= 90000;
    });
    if (dup) continue;
    kept.push(run);
  }
  return kept;
}

function mergeSensorFieldValues(existing = {}, incoming = {}) {
  const left = sanitizeSensorSnapshot(existing);
  const right = sanitizeSensorSnapshot(incoming);
  return {
    tempAtStart: left.tempAtStart ?? right.tempAtStart,
    humidityAtStart: left.humidityAtStart ?? right.humidityAtStart,
    soilAtStart: left.soilAtStart ?? right.soilAtStart,
  };
}

function mergePumpRuns(primary = [], fallback = [], { commands = [], sensorMap = {}, liveSnapshot = null } = {}) {
  const map = new Map();

  const put = (r) => {
    const normalized = normalizePumpRun(r);
    if (!normalized.queuedAt && !normalized.startedAt) return;
    const key = runMergeKey(normalized);
    const existing = map.get(key);
    if (!existing) {
      map.set(key, normalized);
      return;
    }
    const sensors = mergeSensorFieldValues(existing, normalized);
    map.set(key, {
      ...existing,
      ...normalized,
      id: existing.id || normalized.id,
      onCommandId: existing.onCommandId || normalized.onCommandId,
      ...sensors,
    });
  };

  primary.forEach(put);
  fallback.forEach(put);

  return [...map.values()]
    .map((r) => enrichPumpRunSensors(r, { commands, sensorMap, liveSnapshot }))
    .sort((a, b) => (Number(b.queuedAt) || 0) - (Number(a.queuedAt) || 0));
}

function mergeLogs(primary = [], fallback = []) {
  const seen = new Set(primary.map((l) => String(l.id)));
  const extra = fallback.filter((l) => !seen.has(String(l.id)) && normalizeTimestampMs(l.at));
  return [...primary, ...extra].sort((a, b) => (Number(b.at) || 0) - (Number(a.at) || 0));
}

export function subscribeDeviceHistory(deviceId, onData, onError) {
  if (!deviceId) return () => {};

  let cancelled = false;

  const poll = async () => {
    try {
      const [data, commandData, liveData] = await Promise.all([
        userApiService.getDeviceHistory(deviceId),
        userApiService.listDeviceCommands(deviceId, { limit: 50 }).catch(() => ({ items: [] })),
        userApiService.getDeviceLive(deviceId).catch(() => null),
      ]);
      if (cancelled) return;

      const commandItems = commandData?.items || [];
      const apiRuns = (data.pumpRuns || []).map((r) => normalizePumpRun(r));
      const syntheticRuns =
        apiRuns.length > 0
          ? []
          : synthesizePumpRunsFromCommands(commandItems).map((r) => ({
              ...r,
              onCommandId: r.onCommandId || r.id,
            }));
      const syntheticLogs = apiRuns.length > 0 ? [] : relayLogsFromCommands(commandItems);

      const map = {};
      (data.items || []).forEach((row) => {
        map[row.timestamp] = {
          Temperature: row.Temperature,
          Humidity: row.Humidity,
          SoilMoisture: row.SoilMoisture,
          savedAt: row.savedAt ?? null,
        };
      });

      onData({
        sensors: map,
        pumpRuns: dedupeNearPumpRuns(
          mergePumpRuns(apiRuns, syntheticRuns, {
            commands: commandItems,
            sensorMap: map,
            liveSnapshot: liveData?.item || null,
          })
        ),
        pumpWarnings: (data.pumpWarnings || []).map((w) => ({
          ...w,
          at: normalizeTimestampMs(w.at),
        })),
        pumpLogs: mergeLogs(data.pumpLogs || [], syntheticLogs).map((l) => ({
          ...l,
          at: normalizeTimestampMs(l.at),
        })),
      });
    } catch (error) {
      if (!cancelled) onError?.(error);
    }
  };

  poll();
  const interval = setInterval(poll, HISTORY_POLL_MS);
  return () => {
    cancelled = true;
    clearInterval(interval);
  };
}
